import { Form, Input, Tooltip, InputNumber, Divider, Typography, Select, Button, Modal, Spin } from "antd";
import { CloseOutlined, SaveOutlined, EyeOutlined, DiffOutlined } from "@ant-design/icons";
import { WomanOutlined } from "@ant-design/icons";
import { useState, useRef } from "react";
import { Dayjs } from "dayjs";
import dayjs from "dayjs";
import BaseService from "../../../../service/base.service";
import notify from "../../../../components/common/notificationHelper";
import Examinations from "../../../../models/examination";
import GynecologicExaminationDetails from "../../../../models/gynecologicExamination/gynecologicExamination";
import GynecologicExaminationPreviewModal from "./GynecologicExaminationPreviewModal";
import GynecologicComparisonDrawer from "./GynecologicComparisonModal";
import { useEffect } from "react";
import { useIsMobile } from "../../../../hooks/useIsMobile";

const { Title } = Typography;
const { Option } = Select;

const initialAnalyses = [
  "AZOT KREAT",
  "BILURUBINA TOTALE INDIREKTE DIREKTE",
  "AST/ALT",
  "ERITROSEDIMENT",
  "FIBRINOGJEN PCR",
  "VIT D 25 OH D",
  "TSH FT3 FT4",
  "TORCH - TOXO IGM IGG",
  "RUBELLA IGM IGG",
  "CMV IGM IGG",
  "HSV",
  "SIFILIZ QUICK TEST OSE IGM",
  "PARVOVIRUS B19 IGM IGG",
  "EBV IGM IGG",
  "VZV IGM IGG",
  "HEP B HBSAG",
  "HCV IGM",
  "HIV AG/AB COMBI",
  "ELEKTROFOREZA E HB",
  "APTT, INR",
  "FACTOR V LEIDEN",
  "KULTURE VAGINALE (GRAM MYK UREAPLASMA MYCOPLMA",
  "AF-GENITAL +KLAMIDIA",
  "SST",
  "HPV",
  "PAP TEST",
  "EKO OBSTETRIKALE +NT (TRANSLUCENCA NUKALE)",
  "EKO MORFOLOGJIKE MINOR JAVA 11-14",
  "BI TEST OSE NIPT",
  "EKO OBSTETRIKALE JAVA 11-14 ME MJEK TE HUAJ",
  "FIBROZA CISTIKE",
  "DUCHENNE &BECKER MUSKULAR DYSTROPHIES",
  "EGZAMINIME TE TJERA",
];

export interface ExaminationFormValues {
  id?: number,
  gestation?: string;
  gynecologicAnamnesis?: string;
  vitaeAnamnesis?: string;
  familyAnamnesis?: string;
  allergies?: string;
  vulvaVagina?: string;
  pelvicExaminationUterus?: string;
  usgUterus?: string;
  usgUterusLength?: string;
  usgUterusHeight?: string;
  endometrialThickness?: string;
  rightOvaryMorphology?: string;
  leftOvaryMorphology?: string;
  diagnosis?: string;
  recommendations?: string[];
}

interface Props {
  isEdit: boolean;
  isModalOpen: boolean;
  onCancel: () => void;
  patientId: string;
  examinationId?: number | null;
  onSuccess: () => void; // parent callback after successful save
}

const GynecologicExaminationModal = ({
  isEdit,
  isModalOpen: open,
  onCancel,
  patientId,
  examinationId: editId,
  onSuccess,
}: Props) => {
  const isMobile = useIsMobile();
  const [analyses, setAnalyses] = useState(initialAnalyses);
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm<ExaminationFormValues>();
  const horizontalPadding = isMobile ? 12 : 24;
  const contentRef = useRef<HTMLDivElement | null>(null);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewData, setPreviewData] = useState<ExaminationFormValues>({});
  const [compareVisible, setCompareVisible] = useState(false);

  const handleEdit = async (examiantion: Examinations) => {
    setLoading(true);
    try {
      const rp = await BaseService.get<GynecologicExaminationDetails>("/gynecologic-examinations/details/", examiantion.id);
      if (rp.Status) {
        const data = rp.Data;
        form.setFieldsValue({
          ...data
        });
      } else {
        console.error("Stats error:", rp.Messages, rp.Exception);
      }
    } finally {
      setLoading(false);

    }
  };

  const openCompare = async () => setCompareVisible(true);

  useEffect(() => {
    if (open) {
      requestAnimationFrame(() => {
        setTimeout(() => {
          if (contentRef.current) {
            contentRef.current.scrollTop = 0;
          } else {
            const modalBody = document.querySelector(".ant-modal-body") as HTMLElement | null;
            if (modalBody) modalBody.scrollTop = 0;
          }
        }, 0);
      });
    }
  }, [open]);

  useEffect(() => {
    if (open && isEdit && editId) {
      // 🔹 Fetch data when modal opens for editing
      handleEdit({ id: editId } as Examinations);
    } else if (open && !isEdit) {
      // 🔹 Reset form when adding a new examination
      form.resetFields();
    }
  }, [open, isEdit, editId]);

  const handleChange = (selectedValues: string[]) => {
    const remainingOptions = initialAnalyses.filter(
      (item) => !selectedValues.includes(item)
    );
    setAnalyses(remainingOptions);
  };

  const openPreview = async () => {
    try {
      const values = await form.validateFields();
      setPreviewData(values); setPreviewVisible(true);
    }
    catch {
      const allValues = form.getFieldsValue(true);
      setPreviewData(allValues);
      setPreviewVisible(true);
    }
  };

  const handleSubmit = async (values: ExaminationFormValues) => {
    try {
      let rp;
      if (isEdit && values.id) {
        rp = await BaseService.update("/gynecologic-examinations/", values.id, values);
      } else {
        rp = await BaseService.create(`/gynecologic-examinations/${patientId}`, values);
      }
      if (rp.Status) {
        notify({
          title: "Success",
          description: isEdit
            ? "Examination has been updated."
            : "Examination has been added.",
          variant: "success",
        });

        onCancel(); // close modal
        onSuccess(); // 🔹 trigger refresh in parent
      } else {
        notify({
          title: "Error",
          description: "Failed to save Examination.",
          variant: "error",
        });
        console.error("Messages:", rp.Messages, "Exception:", rp.Exception);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!open) {
      setAnalyses(initialAnalyses);
    }
  }, [open]);

  return (
    <div>
      <Modal
        title={
          <div
            style={{
              textAlign: "center",
              fontWeight: 600,
              fontSize: "1.5rem",
              color: "#1f1f1f",
              borderBottom: "1px solid #f0f0f0",
              paddingBottom: 12,
            }}
          >
            {isEdit ? (
              <>
                <WomanOutlined style={{ color: "#d46b08", marginRight: 8 }} />
                Edit Gynecologic Examination
              </>
            ) : (
              <>
                <WomanOutlined style={{ color: "#d46b08", marginRight: 8 }} />
                Add Gynecologic Examination
              </>
            )}
            <div
              style={{
                fontSize: 14,
                fontWeight: 400,
                color: "#6b7280",
                marginTop: 4,
              }}
            >
              {isEdit
                ? "Update the gynecologic examination details below"
                : "Fill in the details to add a new gynecologic examination"}
            </div>
          </div>
        }
        centered
        open={open}
        footer={null}
        width={isMobile ? "calc(100vw - 24px)" : "60vw"}
        confirmLoading={loading}
        onCancel={onCancel}
        styles={{
          body: { maxHeight: isMobile ? "75vh" : "80vh", overflowY: "auto", position: "relative" },
        }}
      >
        {loading && (
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              background: "rgba(255,255,255,0.7)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              zIndex: 1000,
            }}
          >
            <Spin size="large" />
          </div>
        )}
        <div style={{ maxHeight: '65vh', overflowY: 'auto', paddingRight: 8 }}>
          <Form <ExaminationFormValues>
            form={form}
            name="validateOnly"
            layout="vertical"
            autoComplete="off"
            onFinish={handleSubmit}
            initialValues={{
              examinationDate: dayjs(),
            }}
            disabled={loading}
            style={{
              padding: isEdit
                ? `16px ${horizontalPadding}px 0`
                : `0px ${horizontalPadding}px 0`,
            }}
          >
            {/* Pregnancy Section */}
            {isEdit && (
              <Form.Item
                name="id"
                label="Examination ID"
                rules={[{ required: true }]}
              >
                <Input disabled />
              </Form.Item>
            )}
            <Title level={4}>Ob-Gyn Medical History</Title>
            <Form.Item label="Gestation" name="gestation">
              <Input />
            </Form.Item>
            <Form.Item label="A.Gynecology" name="gynecologicAnamnesis">
              <Input />
            </Form.Item>
            <Divider />

            <Title level={4}>Medical History</Title>
            <Form.Item label="A.Vitae" name="vitaeAnamnesis">
              <Input />
            </Form.Item>
            <Form.Item label="A.Family" name="familyAnamnesis">
              <Input />
            </Form.Item>
            <Form.Item label="Allergies" name="allergies">
              <Input />
            </Form.Item>
            <Divider />

            <Title level={4}>Gynecologic Exam</Title>
            <Title level={5}>Pelvic Examination</Title>
            <Form.Item label="Vulva/Vagina" name="vulvaVagina">
              <Input />
            </Form.Item>
            <Form.Item label="Uterus" name="pelvicExaminationUterus">
              <Input />
            </Form.Item>
            <Divider />

            <Title level={4}>USG Examination</Title>
            <Form.Item label="Uterus" name="usgUterus">
              <Input />
            </Form.Item>
            <Form.Item label="Length" name="usgUterusLength">
              <Input />
            </Form.Item>
            <Form.Item label="Height" name="usgUterusHeight">
              <Input />
            </Form.Item>
            <Form.Item label="Endometrial Thickness" name="endometrialThickness">
              <Input />
            </Form.Item>

            <Form.Item label="Right Ovary Morphology" name="rightOvaryMorphology">
              <Input />
            </Form.Item>
            <Form.Item label="Left Ovary Morphology" name="leftOvaryMorphology">
              <Input />
            </Form.Item>

            <Divider />

            {/* Measurements Section */}
            <Title level={4}>Diagnosis</Title>
            <Form.Item label="Diagnosis" name="diagnosis">
              <Input.TextArea rows={4} placeholder="Diagnosis" />
            </Form.Item>

            <Divider />

            {/* Recommendation Section */}
            <Title level={4}>Recommendation</Title>
            <Form.Item label="Recommendation" name="recommendations">
              <Select
                mode="tags"
                style={{ width: "100%" }}
                placeholder="Select or type recommendations"
                onChange={handleChange}
                tokenSeparators={[',']}
              >
                {analyses.map((analysis) => (
                  <Option key={analysis} value={analysis}>
                    {analysis}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Form>
        </div>
        <div
          style={{
            position: "sticky",
            bottom: 0,
            background: "#fff",
            padding: "20px 28px",
            display: "flex",
            flexDirection: "column",
            gap: 14,
            borderTop: "1px solid #f0f0f0",
            boxShadow: "0 -4px 12px rgba(0, 0, 0, 0.06)",
            zIndex: 10,
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 16,
            }}
          >
            <div style={{ display: "flex", gap: 12 }}>
              <Tooltip title="Compare with previous examinations">
                <Button
                  icon={<DiffOutlined />}
                  onClick={openCompare}
                  style={{
                    background: "#f9fafb",
                    borderColor: "#e5e7eb",
                  }}
                >
                  Compare
                </Button>

              </Tooltip>
              <Tooltip title="Preview before saving">
                <Button
                  icon={<EyeOutlined />}
                  onClick={openPreview}
                  style={{
                    background: "#f9fafb",
                    borderColor: "#e5e7eb",
                  }}
                >
                  Preview
                </Button>
              </Tooltip>
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <Button
                danger
                icon={<CloseOutlined />}
                onClick={onCancel}
                style={{
                  padding: "0 22px",
                }}
              >
                Cancel
              </Button>
              <Button
                type="primary"
                icon={<SaveOutlined />}
                onClick={() => form.submit()}
                loading={loading}
                style={{
                  padding: "0 26px",
                  fontWeight: 500,
                }}
              >
                {isEdit ? "Update" : "Save"}
              </Button>
            </div>
          </div>
        </div>
      </Modal>
      {/* Preview Modal */}
      <GynecologicExaminationPreviewModal
        open={previewVisible}
        isEdit={isEdit}
        onClose={() => setPreviewVisible(false)}
        previewData={previewData}
      />
      <GynecologicComparisonDrawer
        open={compareVisible}
        onClose={() => setCompareVisible(false)}
        patientId={patientId}
        formValues={form.getFieldsValue(true)}
      />
    </div>
  );
};

export default GynecologicExaminationModal;
